import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@optools/components/ui/alert-dialog';
import { Button } from '@optools/components/ui/button';
import { Input } from '@optools/components/ui/input';
import * as React from 'react';

export interface TypeToConfirmDialogProps {
  actionText?: string;
  children: React.ReactNode;
  description?: React.ReactNode;
  destructive?: boolean;
  matchInstructions?: React.ReactNode;
  onConfirm: () => void;
  stringToMatch: string;
  title?: React.ReactNode;
}

export function TypeToConfirmDialog({
  actionText = 'Confirm',
  children,
  description = 'This action cannot be undone.',
  destructive = false,
  matchInstructions,
  onConfirm,
  stringToMatch,
  title = 'Are you sure?',
}: TypeToConfirmDialogProps) {
  const [inputValue, setInputValue] = React.useState<string>('');

  // Due to this bug: https://github.com/shadcn-ui/ui/issues/494
  // We will need to manage the open state of the dialog until radix fixes the class override bug.
  // The correct way to handle this alert so we get close for free is to wrap the submit button in
  // <AlertDialogAction><Button /></AlertDialogAction>
  const [open, setOpen] = React.useState<boolean>(false);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setInputValue(event.target.value);

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          {typeof title === 'string' ? <AlertDialogTitle>{title}</AlertDialogTitle> : title}
          {typeof description === 'string' ? (
            <AlertDialogDescription>{description}</AlertDialogDescription>
          ) : (
            description
          )}
        </AlertDialogHeader>
        {typeof matchInstructions === 'string' ? <p>{matchInstructions}</p> : matchInstructions}
        <Input type="text" value={inputValue} onChange={handleInputChange} />
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setInputValue('')}>Cancel</AlertDialogCancel>
          <Button
            disabled={inputValue !== stringToMatch}
            variant={destructive ? 'destructive' : 'default'}
            onClick={() => {
              setOpen(false);
              onConfirm();
            }}>
            {actionText}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
